'use client';

import React from 'react';
import { LoadingState } from 'src/components/PulseUI/LoadingState';
import { EmptyState } from 'src/components/PulseUI/EmptyState';
import { ResultsItem } from '../ResultsItem';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useDiscoveryEntityTypeLabel from 'src/hooks/discovery/useDiscoveryEntityTypeLabel';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
interface ResultsContentProps {
  isLoading: boolean;
  results: any[];
}

const ResultsContent: React.FC<ResultsContentProps> = ({
  isLoading,
  results,
}) => {
  const { closeAllButCurrent } = useDrawer();
  const { getTotalFilterCount } = useFilterSets(LOCATION.DISCOVERY);
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const displayText = useDiscoveryEntityTypeLabel('plural');

  if (isLoading) {
    return (
      <LoadingState
        title="Loading Results"
        description="Please wait while we fetch your data..."
      />
    );
  }

  const handleOpenFilters = () => {
    closeAllButCurrent('allFiltersDrawer');
  };

  // Check if any filters or saved volumes are applied
  const hasFiltersApplied = getTotalFilterCount() > 0 || appliedSavedVolume;

  if (!results?.length) {
    // If no filters are applied, show the "Add a filter" empty state
    if (!hasFiltersApplied) {
      return (
        <EmptyState
          icon={<Icon icon={faSliders} size="2xl" />}
          title={`Add a filter to view ${displayText}`}
          description={`For example, add a Specialty filter to view specific ${displayText} in your territory`}
          className="!rounded-none !bg-transparent !border-none !shadow-none"
          action={
            <button
              type="button"
              className="h-9 px-3.5 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-center items-center gap-2 hover:bg-neutral-050 "
              onClick={handleOpenFilters}
              aria-label="Open filters"
            >
              <div className="flex justify-center items-center gap-2">
                <div className="w-3.5 h-4 overflow-hidden flex items-center justify-center">
                  <Icon icon={faSliders} className="w-3 h-3 text-primary" />
                </div>
                <span className="text-primary text-sm font-semibold font-['Inter'] leading-tight">
                  Filters
                </span>
              </div>
            </button>
          }
        />
      );
    } else {
      // If filters are applied but no results, show "No results found" empty state
      return (
        <EmptyState
          icon={<Icon icon={faSliders} size="2xl" />}
          title={`No ${displayText} Found`}
          description={`No ${displayText.toLowerCase()} match your current search criteria. Try adjusting your filters or search terms.`}
          className="!rounded-none !bg-transparent !border-none !shadow-none"
        />
      );
    }
  }

  return (
    <>
      {results.map((provider, index) => {
        return <ResultsItem key={`${provider.id}-${index}`} provider={provider} />;
      })}
    </>
  );
};

export default ResultsContent;
